/**
 * TabList is a container for a list of Tab elements
 */
import React, { ReactElement, cloneElement, KeyboardEvent, Children, Component, createRef, ReactNode } from "react";
import classNames from "classnames";
import ReactResizeDetector from "react-resize-detector/build/withPolyfill";
import { ITabProps } from "./Tab";
import { withTabs, ITabIndicator, IWithTabsProps } from "./Tabs";

export enum TabListAlign {
  Start = "start",
  Center = "center",
  End = "end",
}

interface ITabListProps extends IWithTabsProps {
  children: ReactElement<ITabProps>[];
  className?: string;
  isBordered?: boolean;
  align?: TabListAlign;
  /**
   * A hidden label required for accessibility to describe the
   * list of tabs: e.g. "Settings", or "Style options"
   */
  "aria-label": string;
}

class TabList extends Component<ITabListProps> {
  public static defaultProps = {
    align: TabListAlign.Start,
  };

  private tabListRef = createRef<HTMLDivElement>();

  public componentDidMount(): void {
    this.updateIndicator();
  }

  public componentDidUpdate(prevProps: ITabListProps): void {
    const numTabsChanged = this.props.tabs.tabRefs.length !== prevProps.tabs.tabRefs.length;
    const indexChanged = this.props.tabs.selectedIndex !== prevProps.tabs.selectedIndex;

    if (numTabsChanged || indexChanged) {
      this.updateIndicator();
    }
  }

  private getIndicatorRect = (): ITabIndicator => {
    const { selectedIndex, tabRefs } = this.props.tabs;
    const tabListRect = this.tabListRef.current?.getBoundingClientRect();
    const selected = selectedIndex != null ? tabRefs[selectedIndex] : undefined;
    const selectedTabRect = selected?.current?.getBoundingClientRect();
    const selectedTabRectLeft = selectedTabRect ? selectedTabRect.left : 0;
    const tabListScrollLeft = this.tabListRef.current ? this.tabListRef.current.scrollLeft : 0;
    const tabListRectLeft = tabListRect ? tabListRect.left : 0;
    const left = selectedTabRectLeft + tabListScrollLeft - tabListRectLeft;
    const width = selectedTabRect?.width || 0;
    return { left, width };
  };

  private updateIndicator = (): void => {
    const indicatorRect = this.getIndicatorRect();
    this.props.tabs.setIndicator(indicatorRect);
  };

  public handleKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    const { selectedIndex, setSelectedIndex, tabRefs, focusPanel } = this.props.tabs;
    const numTabs = tabRefs.length;

    switch (event.key) {
      case "ArrowRight":
        const nextIndex = selectedIndex != null ? (selectedIndex + 1) % numTabs : 0;
        tabRefs[nextIndex].current?.focus();
        setSelectedIndex(nextIndex);
        break;
      case "ArrowLeft":
        const prevIndex = selectedIndex != null ? (selectedIndex - 1 + numTabs) % numTabs : 0;
        tabRefs[prevIndex].current?.focus();
        setSelectedIndex(prevIndex);
        break;
      case "End":
        const lastIndex = numTabs - 1;
        tabRefs[lastIndex].current?.focus();
        setSelectedIndex(lastIndex);
        break;
      case "Home":
        tabRefs[0].current?.focus();
        setSelectedIndex(0);
        break;
      case "ArrowDown":
        event.preventDefault();
        focusPanel();
        break;
    }
  };

  public render(): ReactNode {
    const { isBordered, align, className, children, tabs, "aria-label": ariaLabel } = this.props;
    const { indicator } = tabs;

    // Prevent pop-in on the indicator by getting the inital rect directly in the render,
    // then using indicator from context on subsequent renders
    const currentIndicator = indicator?.width ? indicator : this.getIndicatorRect();

    return (
      <div
        className={classNames("tabs__tab-list", className, {
          "tabs__tab-list--bordered": isBordered,
        })}
      >
        <div className="tabs__tab-list-inner" ref={this.tabListRef} onKeyDown={this.handleKeyDown}>
          <ReactResizeDetector handleWidth onResize={this.updateIndicator} />
          {!!currentIndicator?.width && (
            <div
              className="tabs__indicator"
              style={{
                transform: `translate3d(${currentIndicator?.left}px, 0, 0)`,
                width: currentIndicator?.width,
              }}
            />
          )}
          <div
            className={`tabs__tab-list-items tabs__tab-list-items--align-${align}`}
            role="tablist"
            aria-orientation="horizontal"
            aria-label={ariaLabel}
          >
            {Children.toArray(children)
              .filter(Boolean)
              .map((child, index) => cloneElement(child as ReactElement, { index }))}
          </div>
        </div>
      </div>
    );
  }
}

export default withTabs(TabList);
